import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get } from '../../core/http';
import { API } from '../../core/constants';
import ArticleForm from './ArticleForm';
import ImportArticleDialog from './ImportArticleDialog';
import PageHeader from '../ui/PageHeader';
import Badge from '../ui/Badge';

const ArticlesList = () => {
  const [visible, setVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState();

  const navigate = useNavigate();
  const { search } = useLocation();
  const eventIdFilter = new URLSearchParams(search).get('eventId') || '';

  const getArticles = useCallback(async () => {
    const eventId = new URLSearchParams(search);

    try {
      const result = await get(`${API}/articles?${eventId}`);

      if (result.body) {
        setArticles(result.body);
      }
    } catch (error) {
      console.log('Could not retrieve articles');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    getArticles();
  }, [getArticles]);

  const addNewArticle = async () => {
    setSelectedArticle(null);
    setVisible(true);
  };

  const editArticle = (selected) => {
    setSelectedArticle(selected);
    setVisible(true);
  };

  const handleFormSubmit = async (article) => {
    await getArticles();
    setSelectedArticle(null);
    setVisible(false);
  };

  const handleImported = async (article) => {
    setImporting(false);

    if (article.collection === 'unsorted') {
      // No event was picked at import time, so it went into the shared triage queue
      // instead of straight into articles — send the admin there to assign/edit it.
      navigate('/unsorted');
      return;
    }

    await getArticles();
    // Open the freshly-created draft straight in the editor so it can be reviewed,
    // corrected, and published without a second trip through the list.
    setSelectedArticle(article);
    setVisible(true);
  };

  const dialogs = (
    <>
      {visible && <ArticleForm article={selectedArticle} onFormSubmit={handleFormSubmit} onCancel={() => setVisible(false)} />}
      {importing && (
        <ImportArticleDialog
          defaultEventId={eventIdFilter}
          onImported={handleImported}
          onCancel={() => setImporting(false)}
        />
      )}
    </>
  );

  return (
    <>
      <PageHeader
        title="Articles"
        count={!loading ? articles.length : null}
        actions={
          <>
            <button className="btn btn-outline btn-sm" onClick={() => setImporting(true)}>
              Import from URL
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => addNewArticle()}>
              Add new
            </button>
          </>
        }
      />

      <div className="bg-base-100 rounded-lg border border-base-300 overflow-x-auto">
        {loading && !articles.length ? (
          <p className="text-center text-sm text-base-content/60 py-10">Retrieving articles...</p>
        ) : !articles.length ? (
          <p className="text-center text-sm text-base-content/60 py-10">No articles found for specified filter</p>
        ) : (
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.id}>
                  <td>
                    <div className="font-medium">{article.title}</div>
                    <div className="text-xs text-base-content/50">{article.id}</div>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {!article.eventId && <Badge tone="error">No event ID</Badge>}
                      {article.status === 'draft' && <Badge tone="warning">Draft</Badge>}
                      {article.status === 'private' && <Badge tone="info">Private</Badge>}
                      {article.aiGenerated && <Badge tone="accent">AI-imported</Badge>}
                    </div>
                  </td>
                  <td>
                    <div className="flex justify-end">
                      <button onClick={() => editArticle(article)} className="btn btn-secondary btn-xs">
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialogs}
    </>
  );
};

export default ArticlesList;
